/**
 * Budgets API service
 * Handles all budget-related API operations
 */

import { httpClient } from '@/utils/httpClient';
import { config } from '@/config';
import type {
    APIBudget,
    APIBudgetRequest,
    DRFPaginatedResponse,
    BudgetStatus,
} from '@/types';

const buildQuery = (params: Record<string, unknown>): string => {
    const usp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null) usp.append(k, String(v));
    });
    const s = usp.toString();
    return s ? `?${s}` : '';
};

export interface BudgetListParams {
    page?: number;
    page_size?: number;
    status?: BudgetStatus;
    date_from?: string;
    date_to?: string;
    valid_until_from?: string;
    valid_until_to?: string;
}

export const budgetsService = {
    async list(params?: BudgetListParams): Promise<DRFPaginatedResponse<APIBudget>> {
        const qs = buildQuery((params ?? {}) as Record<string, unknown>);
        return httpClient.get<DRFPaginatedResponse<APIBudget>>(`/budgets/${qs}`);
    },

    async getById(id: number): Promise<APIBudget> {
        return httpClient.get<APIBudget>(`/budgets/${id}/`);
    },

    async create(data: APIBudgetRequest): Promise<APIBudget> {
        return httpClient.post<APIBudget, APIBudgetRequest>('/budgets/', data, {
            successTitle: 'Presupuesto creado',
            successMessage: 'El presupuesto se guardó correctamente.',
            errorTitle: 'Error en presupuesto',
        });
    },

    async update(id: number, data: APIBudgetRequest): Promise<APIBudget> {
        return httpClient.put<APIBudget, APIBudgetRequest>(`/budgets/${id}/`, data, {
            successTitle: 'Presupuesto actualizado',
            successMessage: 'Los cambios del presupuesto se guardaron.',
            errorTitle: 'Error en presupuesto',
        });
    },

    async patch(id: number, data: Partial<APIBudgetRequest>): Promise<APIBudget> {
        return httpClient.patch<APIBudget, Partial<APIBudgetRequest>>(`/budgets/${id}/`, data, {
            successTitle: 'Presupuesto actualizado',
            successMessage: 'Se aplicaron los cambios del presupuesto.',
            errorTitle: 'Error en presupuesto',
        });
    },

    async remove(id: number): Promise<void> {
        return httpClient.delete<void>(`/budgets/${id}/`, {
            successTitle: 'Presupuesto eliminado',
            successMessage: 'El presupuesto se eliminó correctamente.',
            errorTitle: 'Error en presupuesto',
        });
    },

    /**
     * Send budget by email
     * POST /budgets/{id}/send-email/
     */
    async sendEmail(id: number): Promise<void> {
        return httpClient.post<void, Record<string, never>>(`/budgets/${id}/send-email/`, {}, {
            successTitle: 'Presupuesto enviado',
            successMessage: 'El correo se envió correctamente al cliente.',
            errorTitle: 'Error al enviar presupuesto',
        });
    },

    /**
     * Descarga el PDF del presupuesto generado por el backend.
     * GET /budgets/{id}/export-pdf/
     */
    async exportPdf(id: number, fileName?: string): Promise<void> {
        const baseUrl = config.api.baseUrl.replace(/\/$/, '');
        const url = `${baseUrl}/budgets/${id}/export-pdf/`;
        const token = (() => {
            try {
                return localStorage.getItem('access_token');
            } catch {
                return null;
            }
        })();

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                Accept: '*/*',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        });

        if (!response.ok) {
            throw new Error(`No se pudo exportar el presupuesto (HTTP ${response.status}).`);
        }

        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = fileName ?? `presupuesto-${id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
    },

    /**
     * Get public budget by token (no auth required)
     * GET /budgets/public/{token}/
     */
    async getPublic(token: string): Promise<unknown> {
        return httpClient.get<unknown>(`/budgets/public/${token}/`);
    },

    /**
     * Respond to public budget by token (no auth required)
     * POST /budgets/public/{token}/
     */
    async respondPublic(
        token: string,
        data?: { status?: string; rejection_reason?: string | null }
    ): Promise<void> {
        return httpClient.post<void, { status?: string; rejection_reason?: string | null }>(
            `/budgets/public/${token}/`,
            data ?? {},
            {
                successTitle: 'Respuesta recibida',
                successMessage: 'Tu respuesta al presupuesto se registró correctamente.',
                errorTitle: 'Error al responder presupuesto',
            }
        );
    },
};

export default budgetsService;
